import type {
  PackageFilters,
  PackageUsageFilters,
} from "@/types/inventory/package";
import type { PurchaseOrderFilters } from "@/types/inventory/purchase-order";
import type { StockFilters } from "@/types/inventory/stock";
import type { SupplierFilters } from "@/types/inventory/supplier";

import { packageUsageFilterDefinitions } from "@/features/inventory/filter-definitions";
import { serializeFilterQuerySegments } from "@/features/table-filters/contract";

function appendScalar(
  params: URLSearchParams,
  key: string,
  value: unknown,
): void {
  if (value === undefined || value === null || value === "") return;
  params.append(key, String(value));
}

function appendRepeated(
  params: URLSearchParams,
  key: string,
  value: unknown,
): void {
  if (Array.isArray(value)) {
    value.forEach((item) => appendScalar(params, key, item));
    return;
  }
  appendScalar(params, key, value);
}

function appendRange(
  params: URLSearchParams,
  key: string,
  value: unknown,
): void {
  if (!Array.isArray(value)) return;
  const hasBound = value.some(
    (item) => item !== undefined && item !== null && item !== "",
  );
  if (!hasBound) return;
  params.append(key, value[0] == null ? "" : String(value[0]));
  params.append(key, value[1] == null ? "" : String(value[1]));
}

function appendPageParams(
  params: URLSearchParams,
  filters: {
    page?: number;
    perPage?: number;
    sortBy?: string;
    sortDirection?: "asc" | "desc";
    locationId?: string;
  },
): void {
  appendScalar(params, "page", filters.page);
  appendScalar(params, "per_page", filters.perPage);
  appendScalar(params, "sort_by", filters.sortBy);
  appendScalar(params, "sort_direction", filters.sortDirection);
  appendScalar(params, "location_id", filters.locationId);
}

export function packageFiltersToSearchParams(
  filters: PackageFilters = {},
): URLSearchParams {
  const params = new URLSearchParams();
  appendPageParams(params, filters);
  appendRepeated(params, "ids", filters.id ? [filters.id] : undefined);
  appendScalar(params, "name", filters.name);
  appendRange(params, "price", filters.price);
  appendRepeated(params, "contains_items", filters.containsItems);
  appendRange(
    params,
    "number_of_active_patients",
    filters.numberOfActivePatients,
  );
  appendRepeated(params, "category", filters.category);
  appendScalar(params, "category_exact", filters.categoryExact);
  appendRepeated(params, "location_ids", filters.locationIds);
  return params;
}

export function serializePackageUsageFilters(
  filters: PackageUsageFilters = {},
): URLSearchParams {
  return serializeFilterQuerySegments([
    { definitions: packageUsageFilterDefinitions, values: filters },
  ]);
}

export function purchaseOrderFiltersToSearchParams(
  filters: PurchaseOrderFilters = {},
): URLSearchParams {
  const params = new URLSearchParams();
  appendPageParams(params, filters);
  appendRepeated(params, "ids", filters.ids);
  appendRepeated(params, "supplier_id", filters.supplierId);
  appendScalar(params, "stock_id", filters.stockId);
  appendRepeated(params, "location_ids", filters.locationIds);
  appendRepeated(params, "status", filters.status);
  appendScalar(params, "is_archived", filters.isArchived);
  return params;
}

export function stockFiltersToSearchParams(
  filters: StockFilters = {},
): URLSearchParams {
  const params = new URLSearchParams();
  appendPageParams(params, filters);
  appendScalar(params, "name", filters.name);
  appendRepeated(params, "ids", filters.ids);
  appendScalar(params, "item_id", filters.itemId);
  appendRepeated(params, "item_ids", filters.itemIds);
  appendScalar(params, "supplier_id", filters.supplierId);
  appendRepeated(params, "category", filters.category);
  appendRepeated(params, "location_ids", filters.locationIds);
  appendScalar(params, "unit", filters.unit);
  appendScalar(params, "is_archived", filters.isArchived);
  appendRange(params, "remainingQuantity", filters.remainingQuantity);
  appendScalar(params, "group_by", filters.groupBy);
  appendScalar(params, "category_exact", filters.categoryExact);
  appendRange(params, "expiringWithinDays", filters.expiringWithinDays);
  appendRepeated(params, "stockLevelStatus", filters.stockLevelStatus);
  appendScalar(params, "coupa_linked", filters.coupaLinked);
  appendScalar(params, "coupa_expensed", filters.coupaExpensed);
  return params;
}

export function supplierFiltersToSearchParams(
  filters: SupplierFilters = {},
): URLSearchParams {
  const params = new URLSearchParams();
  appendPageParams(params, filters);
  appendScalar(params, "name", filters.name);
  appendScalar(params, "city", filters.city);
  appendScalar(params, "state", filters.state);
  appendScalar(params, "is_archived", filters.isArchived);
  return params;
}
